/**
 * Configuration color constants for Provider sections.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for ALL theme colors (light AND dark).
 * Every component that renders themed colors reads exclusively from here.
 * No color class should be hard-coded in any component file.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface ColorTheme {
  // ── ConfigToggle ─────────────────────────────────────────────────────────
  /** Gradient from/via/to for the CHECKED card background (light + dark) */
  toggleCheckedBg: string;
  /** Border class for the CHECKED card (light + dark) */
  toggleCheckedBorder: string;
  /** Label text color when the toggle IS checked (light + dark) */
  toggleCheckedText: string;
  /** Icon container bg gradient when the toggle IS checked */
  toggleCheckedIconBg: string;

  // ── ConfigInput ───────────────────────────────────────────────────────────
  /** CSS color value for the focus border e.g. '#3b82f6' or 'rgb(...)' */
  focusRingColor: string;
  /** Icon / label text-color class (light + dark) */
  iconColor: string;

  // ── Material-Tailwind Switch ──────────────────────────────────────────────
  switchColor: 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal';

  // ── ConfigAccordion ───────────────────────────────────────────────────────
  /** Accordion header icon container background gradient */
  accordionIcon: string;
  /** Accordion title text class when section is open / hovered (light + dark) */
  accordionTitle: string;

  // ── Sub-section rows  (Alerts / SmartCoolers nested rows) ─────────────────
  /** Sub-row icon container background gradient */
  subSectionIconBg: string;

  // ── ConfigDropdown ────────────────────────────────────────────────────────
  /** Selected / active item background in dropdowns (light + dark) */
  itemSelectedBg: string;
  /** focus:border-* class passed to ConfigDropdown → Autocomplete */
  accentFocusBorder: string;
  /** from-X via-Y to-Z gradient classes for the selected chip in the dropdown */
  dropdownSelectedGradient: string;
  /** Accent bg class for dropdown badges, icon wrappers, empty-state fills (e.g. 'bg-blue-500/10') */
  accentBg: string;
  /** Accent bg class for the option-list divider line (e.g. 'bg-blue-500/40') */
  accentDivider: string;
}

export const CONFIG_COLORS = {
  MMS: {
    // ConfigToggle
    toggleCheckedBg:
      'from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-blue-900/20',
    toggleCheckedBorder: 'border-blue-500 dark:border-blue-400',
    toggleCheckedText: 'text-blue-600 dark:text-blue-400',
    toggleCheckedIconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    // ConfigInput
    focusRingColor: '#3b82f6',
    iconColor: 'text-blue-600 dark:text-blue-400',
    // Switch
    switchColor: 'blue' as const,
    // ConfigAccordion
    accordionIcon: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    accordionTitle: 'text-blue-600 dark:text-blue-400',
    // Sub-section rows
    subSectionIconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    // ConfigDropdown
    itemSelectedBg: 'bg-blue-50 dark:bg-blue-900/20',
    accentFocusBorder: 'focus:border-blue-500',
    dropdownSelectedGradient: 'from-blue-500 via-indigo-500 to-blue-600',
    accentBg: 'bg-blue-500/10 dark:bg-blue-500/15',
    accentDivider: 'bg-blue-500/40 dark:bg-blue-500/60',
  },

  VMS: {
    toggleCheckedBg:
      'from-purple-50 via-fuchsia-50 to-pink-50 dark:from-purple-900/20 dark:via-fuchsia-900/20 dark:to-pink-900/20',
    toggleCheckedBorder: 'border-purple-500 dark:border-purple-400',
    toggleCheckedText: 'text-purple-600 dark:text-purple-400',
    toggleCheckedIconBg: 'bg-gradient-to-br from-purple-500 to-pink-600',
    focusRingColor: '#a855f7',
    iconColor: 'text-purple-600 dark:text-purple-400',
    switchColor: 'purple' as const,
    accordionIcon: 'bg-gradient-to-br from-purple-500 to-pink-600',
    accordionTitle: 'text-purple-600 dark:text-purple-400',
    subSectionIconBg: 'bg-gradient-to-br from-purple-500 to-pink-600',
    itemSelectedBg: 'bg-purple-50 dark:bg-purple-900/20',
    accentFocusBorder: 'focus:border-purple-500',
    dropdownSelectedGradient: 'from-purple-500 via-fuchsia-500 to-pink-600',
    accentBg: 'bg-purple-500/10 dark:bg-purple-500/15',
    accentDivider: 'bg-purple-500/40 dark:bg-purple-500/60',
  },

  CLIENTS: {
    toggleCheckedBg:
      'from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20',
    toggleCheckedBorder: 'border-green-500 dark:border-green-400',
    toggleCheckedText: 'text-green-600 dark:text-green-400',
    toggleCheckedIconBg: 'bg-gradient-to-br from-green-500 to-teal-600',
    focusRingColor: '#22c55e',
    iconColor: 'text-green-600 dark:text-green-400',
    switchColor: 'green' as const,
    accordionIcon: 'bg-gradient-to-br from-green-500 to-teal-600',
    accordionTitle: 'text-green-600 dark:text-green-400',
    subSectionIconBg: 'bg-gradient-to-br from-green-500 to-teal-600',
    itemSelectedBg: 'bg-green-50 dark:bg-green-900/20',
    accentFocusBorder: 'focus:border-green-500',
    dropdownSelectedGradient: 'from-green-500 via-emerald-500 to-teal-600',
    accentBg: 'bg-green-500/10 dark:bg-green-500/15',
    accentDivider: 'bg-green-500/40 dark:bg-green-500/60',
  },

  ALERTS: {
    toggleCheckedBg:
      'from-orange-50 via-red-50 to-orange-100 dark:from-orange-900/20 dark:via-red-900/20 dark:to-orange-900/20',
    toggleCheckedBorder: 'border-orange-500 dark:border-orange-400',
    toggleCheckedText: 'text-orange-600 dark:text-orange-400',
    toggleCheckedIconBg: 'bg-gradient-to-br from-orange-500 to-red-600',
    focusRingColor: '#f97316',
    iconColor: 'text-orange-600 dark:text-orange-400',
    switchColor: 'orange' as const,
    accordionIcon: 'bg-gradient-to-br from-orange-500 to-red-600',
    accordionTitle: 'text-orange-600 dark:text-orange-400',
    subSectionIconBg: 'bg-gradient-to-br from-orange-500 to-red-600',
    itemSelectedBg: 'bg-orange-50 dark:bg-orange-900/20',
    accentFocusBorder: 'focus:border-orange-500',
    dropdownSelectedGradient: 'from-orange-500 via-red-500 to-orange-600',
    accentBg: 'bg-orange-500/10 dark:bg-orange-500/15',
    accentDivider: 'bg-orange-500/40 dark:bg-orange-500/60',
  },

  SMART_COOLERS: {
    toggleCheckedBg:
      'from-cyan-50 via-sky-50 to-blue-50 dark:from-cyan-900/20 dark:via-sky-900/20 dark:to-blue-900/20',
    toggleCheckedBorder: 'border-cyan-500 dark:border-cyan-400',
    toggleCheckedText: 'text-cyan-600 dark:text-cyan-400',
    toggleCheckedIconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
    focusRingColor: '#06b6d4',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    switchColor: 'teal' as const,
    accordionIcon: 'bg-gradient-to-br from-cyan-500 to-blue-600',
    accordionTitle: 'text-cyan-600 dark:text-cyan-400',
    subSectionIconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
    itemSelectedBg: 'bg-cyan-50 dark:bg-cyan-900/20',
    accentFocusBorder: 'focus:border-cyan-500',
    dropdownSelectedGradient: 'from-cyan-500 via-sky-500 to-blue-600',
    accentBg: 'bg-cyan-500/10 dark:bg-cyan-500/15',
    accentDivider: 'bg-cyan-500/40 dark:bg-cyan-500/60',
  },
} satisfies Record<string, ColorTheme>;

export type ConfigSection = keyof typeof CONFIG_COLORS;
